import { useState, useEffect, useRef } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { useForm, Controller } from "react-hook-form"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import {
  useGetResource,
  useCheckConnection,
  useValidateConnection,
  useDeleteResource,
  useListResourceSchemas,
  useListSchemas,
  useUpdateResource,
} from "@/hooks/useResources"
import { useToast } from "@/hooks/useToast"
import { Loader2, Save, TestTube, AlertCircle, CheckCircle2, Lock, Upload, ChevronLeft, Trash2 } from "lucide-react"
import resourceTypes from "@/lib/data/resources.json"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { createResourceSchema, getDefaultValues, processFormData, resourceFieldVisibility } from "@/lib/utils/resource"
import { toTitleCase } from "@/lib/utils/characters"
import { FormField, ResourceType } from "@/types/resource"
import { ROUTES } from "@/routes/path"
import * as z from "zod"
import { DeleteConfirmationDialog } from "@/components/ui/delete-confirmation-dialog"
import { useQueryClient } from "@tanstack/react-query"
import { MultiSelectDropdown } from "@/components/ui/multi-select-dropdown"

type ResourceSettingsFormValues = z.infer<ReturnType<typeof createResourceSchema>>

export default function ResourceSettingsPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const [isEditing, setIsEditing] = useState(true)
  const [isTesting, setIsTesting] = useState(false)
  const [testResult, setTestResult] = useState<"success" | "error" | null>(null)
  const [selectedResource, setSelectedResource] = useState<ResourceType | null>(null)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [availableSchemas, setAvailableSchemas] = useState<string[]>([])
  const [isLoadingSchemas, setIsLoadingSchemas] = useState(false)
  const [loadedSchemasResourceId, setLoadedSchemasResourceId] = useState<string | null>(null)
  const isListingSchemasRef = useRef(false)

  const { data: resource, isLoading, error } = useGetResource(id || "")

  const checkConnectionMutation = useCheckConnection({
    onSuccess: (success) => {
      setTestResult(success ? "success" : "error")
      toast({
        title: success ? "Connection successful" : "Connection failed",
        description: success
          ? "Your connection configuration is valid and working."
          : "Please check your configuration and try again.",
        variant: success ? "default" : "destructive",
      })
    },
    onError: (error) => {
      setTestResult("error")
      toast({
        title: "Connection test failed",
        description: error?.response?.data?.error || error?.message || "An unknown error occurred.",
        variant: "destructive",
      })
    },
    onSettled: () => {
      setIsTesting(false)
    },
  })

  const validateConnectionMutation = useValidateConnection({
    onSuccess: (success) => {
      setTestResult(success ? "success" : "error")
      toast({
        title: success ? "Connection successful" : "Connection failed",
        description: success
          ? "Your connection configuration is valid and working."
          : "Please check your configuration and try again.",
        variant: success ? "default" : "destructive",
      })
    },
    onError: (error) => {
      setTestResult("error")
      toast({
        title: "Connection test failed",
        description: error?.response?.data?.error || error?.message || "An unknown error occurred.",
        variant: "destructive",
      })
    },
    onSettled: () => {
      setIsTesting(false)
    },
  })

  const deleteResourceMutation = useDeleteResource({
    onSuccess: () => {
      toast({
        title: "Resource deleted",
        description: "The resource has been successfully deleted.",
      })
      queryClient.invalidateQueries({ queryKey: ["resources"] })

      navigate(ROUTES.DASHBOARD.RESOURCES.INDEX)
    },
    onError: (error) => {
      toast({
        title: "Delete failed",
        description: error?.response?.data?.error || error?.message || "Failed to delete resource. Please try again.",
        variant: "destructive",
      })
    },
    onSettled: () => {
      setShowDeleteDialog(false)
    },
  })

  const updateResourceMutation = useUpdateResource({
    onSuccess: () => {
      toast({
        title: "Resource updated",
        description: "Resource settings have been updated successfully.",
      })
      queryClient.invalidateQueries({ queryKey: ["resources"] })
      queryClient.invalidateQueries({ queryKey: ["resources", id] })
      setIsEditing(false)
      navigate(ROUTES.DASHBOARD.RESOURCES.MANAGE(id || ""))
    },
    onError: (error) => {
      toast({
        title: "Update failed",
        description: error?.response?.data?.error || error?.message || "Failed to update resource  Please try again.",
        variant: "destructive",
      })
    },
  })

  const listSchemasMutation = useListSchemas({
    onSuccess: (schemas) => {
      setAvailableSchemas(schemas)
    },
    onError: (error) => {
      toast({
        title: "Failed to load schemas",
        description: error?.response?.data?.error || error?.message || "Could not fetch database schemas.",
        variant: "destructive",
      })
    },
    onSettled: () => {
      setIsLoadingSchemas(false)
    },
  })

  const listResourceSchemasMutation = useListResourceSchemas({
    onSuccess: (schemas) => {
      setAvailableSchemas(schemas)
    },
    onError: (error) => {
      toast({
        title: "Failed to load schemas",
        description: error?.response?.data?.error || error?.message || "Could not fetch database schemas.",
        variant: "destructive",
      })
    },
    onSettled: () => {
      setIsLoadingSchemas(false)
    },
  })

  const {
    register,
    control,
    handleSubmit,
    watch,
    getValues,
    reset,
    setValue,
    formState: { errors, isDirty, touchedFields },
  } = useForm<ResourceSettingsFormValues>({
    resolver: async (data) => {
      if (!selectedResource) {
        return { values: data, errors: {} }
      }

      const schema = createResourceSchema(selectedResource, data, true)
      const result = await schema.safeParseAsync(data)

      if (result.success) {
        return { values: result.data, errors: {} }
      } else {
        const fieldErrors: Record<string, any> = {}
        result.error.errors.forEach((error) => {
          if (error.path.length > 0) {
            const fieldName = error.path[0] as string
            fieldErrors[fieldName] = {
              type: "validation",
              message: error.message,
            }
          }
        })
        return { values: {}, errors: fieldErrors }
      }
    },
    defaultValues: selectedResource ? getDefaultValues(selectedResource) : {},
  })

  const formData = watch()

  // Helper function to get error message
  const getErrorMessage = (fieldName: string): string | undefined => {
    const error = errors[fieldName as keyof typeof errors]
    if (error && typeof error === "object" && "message" in error) {
      return (error as any).message
    }
    return undefined
  }

  // Set selectedResource when resource data changes
  useEffect(() => {
    if (resource) {
      const resourceType = resourceTypes.find((r) => r.id === resource.type)
      if (resourceType) {
        setSelectedResource(resourceType as ResourceType)
      }
    }
  }, [resource])

  // Reset form when resource data changes
  useEffect(() => {
    if (resource && selectedResource) {
      // Create default values from the resource data
      const defaultValues = getDefaultValues(selectedResource)

      // Override with actual resource values
      const resourceValues = {
        ...defaultValues,
        ...resource.meta, // Spread the meta data to populate form fields
        name: resource.name,
        description: resource.description || "",
      }

      reset(resourceValues)
    }
  }, [resource, selectedResource, reset])

  useEffect(() => {
    if (!resource || !selectedResource) return
    if (selectedResource.id !== "postgres" && selectedResource.id !== "supabase") return
    if (loadedSchemasResourceId === resource.id) return
    if (isListingSchemasRef.current) return

    isListingSchemasRef.current = true
    setIsLoadingSchemas(true)
    listResourceSchemasMutation
      .mutateAsync(resource.id)
      .then(() => {
        setLoadedSchemasResourceId(resource.id)
      })
      .finally(() => {
        isListingSchemasRef.current = false
      })
  }, [resource, selectedResource, loadedSchemasResourceId, listResourceSchemasMutation])

  const onSubmit = async (data: ResourceSettingsFormValues) => {
    if (!selectedResource || !id) return

    const processedData = await processFormData(data, selectedResource)

    await updateResourceMutation.mutateAsync({
      resourceId: id,
      data: {
        name: data.name,
        description: data.description || "",
        type: selectedResource.id,
        meta: processedData,
      },
    })
  }

  const handleTestConnection = async () => {
    if (!resource || !selectedResource) return

    setIsTesting(true)
    setTestResult(null)

    // If there are no changes to the form, use check-connection with resource ID
    if (!isDirty) {
      await checkConnectionMutation.mutateAsync({
        resource_id: resource.id,
      })
    } else {
      // If there are changes, use validate-connection with new form data
      await validateConnectionMutation.mutateAsync({
        resource_type: resource.type,
        meta: await processFormData(getValues(), selectedResource),
      })
    }
  }

  const isFieldVisible = (field: FormField): boolean => {
    if (!field.visible) return true

    return resourceFieldVisibility(field.visible, formData)
  }

  const getOrderedFields = (fields: FormField[] = []): FormField[] => {
    const schemaFields = fields.filter((field) => field.name === "schema")
    const otherFields = fields.filter((field) => field.name !== "schema")
    return [...otherFields, ...schemaFields]
  }

  const handleLoadSchemas = async () => {
    if (!selectedResource || (selectedResource.id !== "postgres" && selectedResource.id !== "supabase")) return

    setIsLoadingSchemas(true)
    if (!isDirty && resource?.id) {
      await listResourceSchemasMutation.mutateAsync(resource.id)
      return
    }

    await listSchemasMutation.mutateAsync({
      resource_type: selectedResource.id,
      meta: await processFormData(getValues(), selectedResource),
    })
  }

  const renderField = (field: FormField, keyPath: string) => {
    if (!isFieldVisible(field)) return null
    const fieldKey = field.name || keyPath

    switch (field.type) {
      case "text":
        return (
          <div key={fieldKey} className="space-y-1 py-1">
            <Label htmlFor={field.name}>
              {field.label || toTitleCase(field.name)}
              {field.required && <span className="ml-1 text-red-500">*</span>}
            </Label>
            <Input
              {...register(field.name)}
              id={field.name}
              autoComplete="off"
              placeholder={field.label || field.name}
              error={getErrorMessage(field.name)}
              touched={touchedFields[field.name as keyof typeof touchedFields]}
              disabled={!isEditing}
            />
          </div>
        )

      case "password":
        return (
          <div key={fieldKey} className="space-y-1 py-1">
            <Label htmlFor={field.name} className="flex items-center gap-2">
              {field.label || toTitleCase(field.name)}
              <Lock className="h-3 w-3" />
              <span className="text-xs text-gray-500">Encrypted</span>
              {field.required && !field.encrypted && <span className="ml-1 text-red-500">*</span>}
            </Label>
            <Input
              {...register(field.name)}
              id={field.name}
              type="password"
              placeholder={field.label || field.name}
              autoComplete="off"
              error={getErrorMessage(field.name)}
              touched={touchedFields[field.name as keyof typeof touchedFields]}
              disabled={!isEditing}
            />
          </div>
        )

      case "number":
        return (
          <div key={fieldKey} className="space-y-1 py-2">
            <Label htmlFor={field.name}>
              {field.label || toTitleCase(field.name)}
              {field.required && <span className="ml-1 text-red-500">*</span>}
            </Label>
            <Input
              {...register(field.name)}
              id={field.name}
              type="number"
              placeholder={field.label || field.name}
              autoComplete="off"
              error={getErrorMessage(field.name)}
              touched={touchedFields[field.name as keyof typeof touchedFields]}
              disabled={!isEditing}
            />
          </div>
        )

      case "textarea":
        return (
          <div key={fieldKey} className="space-y-1 py-2">
            <Label htmlFor={field.name} className={field.encrypted ? "flex items-center gap-2" : ""}>
              {field.label || toTitleCase(field.name)}
              {field.encrypted && (
                <>
                  <Lock className="h-3 w-3" />
                  <span className="text-xs text-gray-500">Encrypted</span>
                </>
              )}
              {field.required && <span className="ml-1 text-red-500">*</span>}
            </Label>
            <Textarea
              {...register(field.name)}
              id={field.name}
              placeholder={field.label || field.name}
              autoComplete="off"
              disabled={!isEditing}
            />
            {getErrorMessage(field.name) && <p className="mt-1 text-sm text-red-500">{getErrorMessage(field.name)}</p>}
          </div>
        )

      case "file":
        return (
          <div key={fieldKey} className="space-y-1 py-2">
            <Label htmlFor={field.name} className={field.encrypted ? "flex items-center gap-2" : ""}>
              {field.label || toTitleCase(field.name)}
              {field.encrypted && (
                <>
                  <Lock className="h-3 w-3" />
                  <span className="text-xs text-gray-500">Encrypted</span>
                </>
              )}
              {field.required && <span className="ml-1 text-red-500">*</span>}
            </Label>
            <div className="space-y-2">
              <div className="flex gap-2">
                <Input
                  {...register(field.name)}
                  id={field.name}
                  type="file"
                  className="flex-1"
                  autoComplete="off"
                  disabled={!isEditing}
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) {
                      // Validate file type for certificate files
                      const allowedTypes = [
                        "application/x-pem-file",
                        "application/x-x509-ca-cert",
                        "application/pkix-cert",
                        "text/plain",
                        "application/octet-stream",
                      ]

                      if (
                        !allowedTypes.includes(file.type) &&
                        !file.name.endsWith(".pem") &&
                        !file.name.endsWith(".crt") &&
                        !file.name.endsWith(".key")
                      ) {
                        toast({
                          title: "Invalid file type",
                          description: "Please select a valid certificate file (.pem, .crt, .key)",
                          variant: "destructive",
                        })
                        e.target.value = ""
                        return
                      }
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!isEditing}
                  onClick={() => {
                    const fileInput = document.getElementById(field.name) as HTMLInputElement
                    fileInput?.click()
                  }}
                >
                  <Upload className="mr-1 h-4 w-4" />
                  Select
                </Button>
              </div>
            </div>
            {getErrorMessage(field.name) && <p className="mt-1 text-sm text-red-500">{getErrorMessage(field.name)}</p>}
          </div>
        )

      case "select":
        return (
          <div key={fieldKey} className="space-y-1 py-2">
            <Label htmlFor={field.name}>
              {field.label || toTitleCase(field.name)}
              {field.required && <span className="ml-1 text-red-500">*</span>}
            </Label>
            <Controller
              name={field.name}
              control={control}
              render={({ field: controllerField }) => (
                <Select
                  value={controllerField.value || field.default || ""}
                  onValueChange={controllerField.onChange}
                  disabled={!isEditing}
                >
                  <SelectTrigger className={getErrorMessage(field.name) ? "border-red-500" : ""}>
                    <SelectValue placeholder={`Select ${field.label || field.name}`} />
                  </SelectTrigger>
                  <SelectContent>
                    {field.options?.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {getErrorMessage(field.name) && <p className="mt-1 text-sm text-red-500">{getErrorMessage(field.name)}</p>}
          </div>
        )

      case "multi-select": {
        if (field.name !== "schema") {
          return null
        }

        const selectedSchemas = Array.isArray((formData as Record<string, any>)["schema"])
          ? ((formData as Record<string, any>)["schema"] as string[]) || []
          : []

        return (
          <div key={fieldKey} className="space-y-2 py-2">
            <Label htmlFor={field.name}>{field.label || toTitleCase(field.name)}</Label>

            <MultiSelectDropdown
              options={availableSchemas}
              value={selectedSchemas}
              onChange={(next) => {
                setValue("schema", next as any, {
                  shouldDirty: true,
                  shouldValidate: true,
                  shouldTouch: true,
                })
              }}
              placeholder={isLoadingSchemas ? "Loading schemas..." : "Select schemas"}
              allLabel="All schemas"
              disabled={!isEditing}
              emptyContent={
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">
                    Fetch available schemas from your database to select one, multiple, or all schemas for analysis.
                  </p>
                  <button
                    type="button"
                    className="text-sm text-primary underline underline-offset-4 disabled:opacity-50"
                    onClick={handleLoadSchemas}
                    disabled={isLoadingSchemas || !isEditing}
                  >
                    {isLoadingSchemas ? "Loading schemas..." : "Load schemas"}
                  </button>
                </div>
              }
            />
            {getErrorMessage(field.name) && <p className="mt-1 text-sm text-red-500">{getErrorMessage(field.name)}</p>}
          </div>
        )
      }

      case "tab":
        return (
          <div key={fieldKey} className="space-y-1 py-2">
            <Label className="font-medium text-gray-900 dark:text-white">
              {field.label || toTitleCase(field.name)}
            </Label>
            <Controller
              name={field.name}
              control={control}
              render={({ field: controllerField }) => (
                <div className="mt-2 flex gap-2">
                  {field.options?.map((option) => (
                    <Button
                      key={option.value}
                      type="button"
                      variant={controllerField.value === option.value ? "default" : "outline"}
                      onClick={() => controllerField.onChange(option.value)}
                      size="xs"
                      className="h-7 py-2 text-xs"
                      disabled={!isEditing}
                    >
                      {option.name}
                    </Button>
                  ))}
                </div>
              )}
            />
            {getErrorMessage(field.name) && <p className="mt-1 text-sm text-red-500">{getErrorMessage(field.name)}</p>}
          </div>
        )

      case "flex-row":
        return (
          <div key={fieldKey}>
            {field.label && (
              <Label className="mb-2 block font-medium text-gray-900 dark:text-white">{field.label}</Label>
            )}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-[repeat(auto-fit,minmax(200px,1fr))]">
              {field.fields?.map((nestedField, idx) => renderField(nestedField, `${fieldKey}-${idx}`))}
            </div>
          </div>
        )

      case "group":
        return (
          <div key={fieldKey} className="space-y-1">
            <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
              {field.label || toTitleCase(field.name)}
            </h3>
            {field.fields?.map((nestedField, idx) => renderField(nestedField, `${fieldKey}-${idx}`))}
          </div>
        )

      default:
        return null
    }
  }

  if (isLoading) {
    return (
      <div className="mx-auto max-w-6xl p-6">
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </div>
    )
  }

  if (error || !resource) {
    return (
      <div className="mx-auto max-w-6xl p-6">
        <div className="py-12 text-center">
          <p className="text-gray-500">Failed to load resource </p>
        </div>
      </div>
    )
  }

  const resourceType = resourceTypes.find((rt) => rt.id === resource.type)

  return (
    <div className="mx-auto max-w-6xl px-4 py-2">
      {/* Header with back button */}
      <div className="mb-4">
        <div className="mb-3 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate(ROUTES.DASHBOARD.RESOURCES.MANAGE(id || ""))}
            className="flex items-center gap-1"
          >
            <ChevronLeft className="h-4 w-4" />
            Back
          </Button>
        </div>
        <div className="flex justify-between">
          <div>
            <div className="mb-1 flex items-center gap-3">
              {resourceType && <img src={resourceType.icon} alt={resourceType.name} className="h-5 w-5" />}
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Resource Settings</h1>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Configure your {resourceType?.name || resource.type} connection
            </p>
          </div>
          <Button variant="destructiveOutline" size="sm" onClick={() => setShowDeleteDialog(true)}>
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
          </Button>
        </div>
      </div>

      <div className="max-w-6xl">
        {/* Main form */}
        <div className="space-y-5">
          <form onSubmit={handleSubmit(onSubmit)}>
            {/* Basic information */}
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <Input
                    label="Name"
                    {...register("name")}
                    error={getErrorMessage("name")}
                    touched={touchedFields.name}
                    placeholder={`My ${resourceType?.name || resource.type}`}
                    disabled={!isEditing}
                  />
                </div>
                <div>
                  <Input
                    label="Description (optional)"
                    {...register("description")}
                    error={getErrorMessage("description")}
                    touched={touchedFields.description}
                    placeholder="Production database for analytics"
                    disabled={!isEditing}
                  />
                </div>
              </div>
            </div>

            {/* Dynamic fields based on resource configuration */}
            {getOrderedFields(selectedResource?.fields).map((field, idx) => renderField(field, `field-${idx}`))}

            {/* Connection Status */}
            <div className="my-5 space-y-2">
              <Label>Connection Status</Label>
              <div className="flex items-center gap-2">
                <div
                  className={`h-2 w-2 rounded-full ${
                    resource.status === "active"
                      ? "bg-green-500"
                      : resource.status === "analyzing"
                        ? "bg-yellow-500"
                        : "bg-red-500"
                  }`}
                />
                <span className="text-sm font-medium capitalize text-gray-700 dark:text-gray-300">
                  {resource.status}
                </span>
              </div>
            </div>

            {/* Test Result */}
            {testResult && (
              <Alert variant={testResult === "success" ? "success" : "error"}>
                {testResult === "success" ? <CheckCircle2 className="h-5 w-5" /> : <AlertCircle className="h-5 w-5" />}
                <AlertDescription>
                  <span className="font-medium">
                    {testResult === "success"
                      ? "Connection successful!"
                      : "Connection failed. Please check your configuration."}
                  </span>
                </AlertDescription>
              </Alert>
            )}

            {/* Action buttons */}
            <div className="mb-6 flex justify-between pt-10">
              <Button
                type="button"
                variant="outline"
                onClick={handleTestConnection}
                disabled={
                  resource.status === "analyzing" ||
                  isTesting ||
                  checkConnectionMutation.isPending ||
                  validateConnectionMutation.isPending
                }
              >
                {isTesting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Testing...
                  </>
                ) : (
                  <>
                    <TestTube className="mr-2 h-4 w-4" />
                    Test Connection
                  </>
                )}
              </Button>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(ROUTES.DASHBOARD.RESOURCES.MANAGE(id || ""))}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={!isDirty || updateResourceMutation.isPending || isTesting}>
                  {updateResourceMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        title="Delete Resource"
        description="Are you sure you want to delete {itemName}? This action cannot be undone and will permanently remove:"
        itemName={resource?.name || ""}
        onConfirm={() => {
          if (id) {
            deleteResourceMutation.mutate(id)
          }
        }}
        isDeleting={deleteResourceMutation.isPending}
        confirmButtonText="Delete Resource"
        itemsToDelete={[
          "The resource connection",
          "All associated tables and fields",
          "All field configurations and mappings",
        ]}
      />
    </div>
  )
}
